import * as React from 'react';
import { Action } from '@liveauctioneers/caterwaul-components/lib/hooks/queryParams';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import {
    getTotalSavedSearches,
    isSavedSearchesLoading,
    onSavedSearchRecentFilterChange,
    savedSearchSearchesSelector,
    subscribeAllSavedSearchEmail,
    toggleSavedSearchEmail,
    unsubscribeAllSavedSearchEmail,
} from '@/redux/modules/savedSearch';
import { ModifyQueryParamsAction } from '@/hooks/queryParams/useModifyQueryParams';
import {
    QueryParams as NotificationsPageQueryParams,
    SetActiveIdCallback,
} from '@/pages/Notifications/notifications.types';
import { PaginationFilter } from '@/components/Pagination/hammer-pagination/types/PaginationFilter';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useQueryParams } from '@/hooks/queryParams';
import { useSearchParams } from 'react-router-dom';
import isEqual from 'react-fast-compare';
import Pagination from '@/components/Pagination/hammer-pagination/Pagination';
import SavedSearchNotificationFilter from '../ForYou/ForYouPage/components/MySavedSearch/components/SavedSearchNotificationFilter';
import SavedSearchTable from './Components/SavedSearchTable';
import Section from './Components/Section';
import StatusCheckbox from './Components/StatusCheckbox';
import styled from 'styled-components';

type QueryParams = { page?: string; pageSize?: string };

type Props = {
    activeId: NotificationsPageQueryParams['section'];
    filters: PaginationFilter;
    queryParams: QueryParams;
    setActiveId: SetActiveIdCallback;
};

const MySavedSearchPageManage = ({ activeId, filters, setActiveId }: Props) => {
    const dispatch = useAppDispatch();

    const totalSavedSearchCount = useAppSelector(getTotalSavedSearches);
    const isLoading = useAppSelector(isSavedSearchesLoading);
    const savedSearches = useAppSelector(savedSearchSearchesSelector);
    const { modifyQueryParams } = useQueryParams();
    const [queryParams] = useSearchParams();
    const section = queryParams.get('section');

    const toggleEmail = React.useCallback(
        (savedSearchId) => dispatch(toggleSavedSearchEmail(savedSearchId)),
        [dispatch]
    );

    React.useEffect(() => {
        if (!section) {
            modifyQueryParams({
                action: Action.ADD,
                query: { section: 'searches' },
            });
        }
    }, [modifyQueryParams, section]);

    const onFilterChange = React.useCallback(
        (filters: PaginationFilter) => {
            // modify url with pageSize and page query params
            modifyQueryParams({
                action: ModifyQueryParamsAction.ADD,
                query: { page: filters.page, pageSize: filters.pageSize },
            });
            dispatch(onSavedSearchRecentFilterChange(filters));
        },
        [dispatch, modifyQueryParams]
    );

    const showPager = typeof filters.pageSize === 'number' && totalSavedSearchCount > filters.pageSize;

    const filterComponent = (
        <SavedSearchNotificationFilter
            filters={filters}
            onFilterChange={onFilterChange}
        />
    );

    return (
        <Section
            activeId={activeId}
            component={
                <SearchManageContainer>
                    {showPager ? (
                        <Pagination
                            filter={filterComponent}
                            onChange={onFilterChange}
                            paginationFilter={{
                                ...filters,
                                totalRecords: totalSavedSearchCount,
                            }}
                            shouldModifyQueryParams
                        >
                            <SavedSearchTable
                                searches={savedSearches}
                                toggleSavedSearchEmail={toggleEmail}
                            />
                        </Pagination>
                    ) : (
                        <>
                            {filterComponent}
                            <>
                                {!isLoading && totalSavedSearchCount === 0 ? (
                                    <NoFollowed>
                                        <FormattedMessage
                                            id="search_alerts.no_followed"
                                            key="0"
                                        />
                                    </NoFollowed>
                                ) : (
                                    <>
                                        <SavedSearchTable
                                            searches={savedSearches}
                                            toggleSavedSearchEmail={toggleEmail}
                                        />
                                    </>
                                )}
                            </>
                        </>
                    )}
                </SearchManageContainer>
            }
            count={totalSavedSearchCount}
            email={
                /*  Slice because the last one is an unsubscribe button */
                <StatusCheckbox
                    array={savedSearches}
                    category="saved search"
                    onChange={(checked) => {
                        checked ? dispatch(unsubscribeAllSavedSearchEmail()) : dispatch(subscribeAllSavedSearchEmail());
                    }}
                    selector={(search) => search.sendAlert}
                    type="email"
                />
            }
            id="searches"
            setActiveId={setActiveId}
        />
    );
};

export default React.memo<Props>(MySavedSearchPageManage, isEqual);

const SearchManageContainer = styled.div`
    display: block;
    max-width: 1200px;
    align-self: center;
    width: 100%;
    margin: 0 auto 10px;
    padding-bottom: 32px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.grey400};
`;

const NoFollowed = styled.div`
    margin-top: 32px;
    text-align: center;
    size: 14px;
    letter-spacing: 0.23px;
`;
